import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { type TFunction } from "i18next";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import Granularity from "src/constants/granularity.enum";
import IntervalType from "src/constants/intervalType.enum";
import {
  type MeasureItemDto,
  type MeasuresItemizedCompactItemDto,
  type MeasuresItemizedCompactDto,
  type MeasuresSummarizedDto,
  MeasuresWithHasData,
} from "src/types/dashboardDatasetDto";
import { MeasuresItemizedItemDto, type MeasuresItemizedDto } from "src/types/locationChartDto";

dayjs.extend(isoWeek);

export const getToday = () => dayjs();
export const getYesterday = () => dayjs().add(-1, "day");
export const getTomorrow = () => dayjs().add(1, "day");


export function buildChartData(
  measuresData: MeasuresItemizedDto | MeasuresItemizedCompactDto | undefined | null,
  type: string,
  selectedTab: IntervalTab = IntervalTab.Day
): MeasuresWithHasData<MeasuresItemizedItemDto>[] | MeasuresItemizedCompactItemDto[] {
  const intervalType = getIntervalFromSelectedTab(selectedTab);
  const granularity = getGranularityFromInterval(intervalType);
  const end = measuresData?.currentDateRange?.end;

  if (!measuresData) return [];

  let fullDateRange: string[] = [];
  let pastDateRange: string[] = [];

  if (type === "itemized") {
    fullDateRange = generateDateRange(measuresData.currentDateRange?.start, end, granularity);
    pastDateRange = generateDateRange(measuresData.pastDateRange?.start, measuresData.pastDateRange?.end, granularity);

    const values = extractItemizedValues(measuresData as MeasuresItemizedDto);

    return fillMissingItemizedDates(fullDateRange, pastDateRange, values);
  }

  if (type === "compact") {
    fullDateRange = generateDateRange(measuresData.currentDateRange?.start, end, granularity);
    pastDateRange = generateDateRange(measuresData.pastDateRange?.start, measuresData.pastDateRange?.end, granularity);

    const values = extractCompactValues(measuresData as MeasuresItemizedCompactDto);

    return fillMissingCompactDates(fullDateRange, pastDateRange, values);
  }
  return [];
}

const extractItemizedValues = (data: MeasuresItemizedDto): MeasuresWithHasData<MeasuresItemizedItemDto>[] => {
  if (!data || !Array.isArray(data.data)) return [];

  return data.data.map((item) => ({
    hasData: true,
    ...item,
  }));
};

const extractCompactValues = (data: MeasuresItemizedCompactDto): MeasuresItemizedCompactItemDto[] => {
  if (!data || !Array.isArray(data.data)) return [];

  return data.data.map((item) => {
    const currentValues =
      item.currentValues?.map((cv) => ({
        ...cv,
        hasData: true,
      })) ?? [];

    return {
      ...item,
      currentValues,
    };
  });
};

const generateDateRange = (start?: Date, end?: Date, granularity: Granularity = Granularity.Day): string[] => {
  if (!start || !end) return [];

  const dates: string[] = [];
  const current = new Date(start);
  const endDate = new Date(end);

  while (current < endDate) {
    dates.push(dayjs(current).format("YYYY-MM-DDTHH:mm:ss"));

    switch (granularity) {
      case Granularity.Hour:
        current.setHours(current.getHours() + 1);
        break;
      case Granularity.Day:
        current.setDate(current.getDate() + 1);
        break;
      case Granularity.Week:
        current.setDate(current.getDate() + 7);
        break;
    }
  }

  return dates;
};

const fillMissingItemizedDates = (
  dates: string[],
  pastDates: string[],
  values: MeasuresWithHasData<MeasuresItemizedItemDto>[]
): MeasuresWithHasData<MeasuresItemizedItemDto>[] => {
  const valuesByDate = new Map(values.map((v) => [v.currentDate, v]));

  return dates.map((date, index) => {
    const entry = valuesByDate.get(date);
    
    // display pastDate and currentDate of entry if an entry Exists, else it generates an empty entry with the corresponding dates: 
    // There is an edge case for ISO year charts when the current year has 53 weeks and only 52 weeks in the prior year, 
    // in this case it falls back to KW 52 for KW 53 (Requirements of DR-2751)
    return entry
      ? { ...entry, pastDate: entry.pastDate, pastValue: entry.pastValue ?? 0 }
      : {
          measureTime: date,
          count: 0,
          hasData: false,
          isIncreased: false,
          unchangedValue: 0,
          currentDate: date,
          pastDate: pastDates[Math.min(index, pastDates.length - 1)] ?? "",
          pastValue: 0,
          status: "",
          temperature: "",
          pastStatus: "",
          pastTemperature: "",
          event: "",
          eventDescription: "",
          pastEvent: "",
          pastEventDescription: "",
        };
  });
};

/**
 * Returns all values for each date, or an empty placeholder if there is none.
 * A date can hold several values (ISO week 53 is compared with week 52, so its past date repeats).
 */
const fillMissingMeasures = (dates: string[], values: MeasureItemDto[] | null = []): MeasureItemDto[] => {
  const valuesByDate = new Map<string, MeasureItemDto[]>();
  for (const value of values ?? []) {
    const date = dayjs(value.measureTime).format("YYYY-MM-DDTHH:mm:ss");
    valuesByDate.set(date, [...(valuesByDate.get(date) ?? []), value]);
  }

  return dates.flatMap(
    (date) => valuesByDate.get(date) ?? [{ 
      measureTime: date, 
      hasData: false, 
      count: 0 
    } as unknown as MeasureItemDto]
  );
};

const fillMissingCompactDates = (
  dates: string[],
  pastDates: string[],
  values: MeasuresItemizedCompactItemDto[]
): MeasuresItemizedCompactItemDto[] =>
  values.map((item) => ({
    ...item,
    currentValues: fillMissingMeasures(dates, item.currentValues),
    pastValues: fillMissingMeasures(pastDates, item.pastValues),
  }));

/**
 * Returns Echarts formatter string for time Axis.
 *
 * @link https://echarts.apache.org/en/option.html#xAxis.axisLabel.formatter
 * @param intervalType interval type of chart data
 * @returns Echarts formatter String
 */
export function getFormatterString(intervalType: IntervalType): string {
  switch (intervalType) {
    case IntervalType.Hour: {
      return "{HH}";
    }
    case IntervalType.Day: {
      return "{HH}";
    }
    case IntervalType.Week: {
      return "{ee}";
    }
    case IntervalType.Month: {
      return "{d}";
    }
    case IntervalType.IsoYear: {
      return "{MMMM}";
    }
    default: {
      return "{dd}.{M}.{yyyy}";
    }
  }
}

/**
 * Returns end date for a period by an start day and an interval type
 *
 * @param date date Dayjs Object
 * @param intervalType intervalType which should go back
 * @returns endDate in Dayjs Format
 */
export function subtractIntervalTypeFromDate(date: Dayjs, intervalType: IntervalType): Dayjs {
  switch (intervalType) {
    case IntervalType.Hour: {
      return date.subtract(1, "hour");
    }
    case IntervalType.Day: {
      return date.subtract(1, "day");
    }
    case IntervalType.Week: {
      return date.subtract(1, "week");
    }
    case IntervalType.Month: {
      return date.subtract(1, "month");
    }
    case IntervalType.IsoYear: {
      // Same ISO week and weekday in the previous ISO year; KW53 falls back to the last week (KW52).
      const previousIsoYear = date.isoWeekYear() - 1;
      const week = Math.min(date.isoWeek(), getIsoWeeksInYear(previousIsoYear));
      return getIsoYearAnchor(date, previousIsoYear).isoWeek(week).isoWeekday(date.isoWeekday());
    }
    default: {
      throw new RangeError("No Interval set");
    }
  }
}

/**
 * Returns 4 January of the given ISO year (default: ISO year of the date), keeping the time of day.
 * 4 January always lies in ISO week 1, so calendar year and ISO year of the result are identical.
 *
 * @param date date Dayjs Object
 * @param isoYear target ISO year
 * @returns anchor date inside the ISO year
 */
export function getIsoYearAnchor(date: Dayjs, isoYear: number = date.isoWeekYear()): Dayjs {
  return date.year(isoYear).month(0).date(4);
}

/**
 * Moves a date into the given ISO year, keeping day, month and time of day where possible.
 * If the same calendar day belongs to a neighbouring ISO year (e.g. 30.12.2019 is KW1 of 2020),
 * the nearest day inside the target ISO year is returned (30.12.2019 -> ISO 2019 -> 29.12.2019).
 *
 * @param date date Dayjs Object
 * @param isoYear target ISO year
 * @returns date inside the target ISO year
 */
export function moveToIsoYear(date: Dayjs, isoYear: number): Dayjs {
  const candidate = date.year(isoYear);
  if (candidate.isoWeekYear() === isoYear) return candidate;

  const boundary =
    candidate.isoWeekYear() > isoYear
      ? getEndOfIsoYear(getIsoYearAnchor(date, isoYear))
      : getIsoYearAnchor(date, isoYear).startOf("isoWeek");
  return boundary.hour(date.hour()).minute(date.minute()).second(date.second()).millisecond(date.millisecond());
}

/**
 * Returns the date sent to the API for an interval. 
 * IsoYear: date is moved into ISO year with same number as its calendar year (30.12.2019 -> ISO 2019).
 * Other intervals and null (backend uses today) are returned unchanged.
 *
 * @param intervalType interval type of the query
 * @param date selected date
 * @returns date for the API request
 */
export function getIntervalQueryDate(intervalType: IntervalType, date: Dayjs | null): Dayjs | null {
  if (intervalType !== IntervalType.IsoYear || !date) return date;
  return moveToIsoYear(date, date.year());
}

/**
 * Returns the number of ISO weeks (52 or 53) of an ISO year.
 *
 * @param isoYear ISO year
 * @returns number of ISO weeks
 */
export function getIsoWeeksInYear(isoYear: number): number {
  return dayjs(new Date(isoYear, 11, 28)).isoWeek();
}

/**
 * Returns the end (Sunday 23:59:59.999 of the last ISO week) of the ISO year the date belongs to.
 *
 * @param date date Dayjs Object
 * @returns end of the ISO year
 */
export function getEndOfIsoYear(date: Dayjs): Dayjs {
  return dayjs(new Date(date.isoWeekYear(), 11, 28)).endOf("isoWeek");
}

/**
 * Returns the Granularity for the corresponding interval type
 *
 * @param intervalType used interval type
 * @returns the granularity
 */
export function getGranularityFromInterval(intervalType: IntervalType): Granularity {
  switch (intervalType) {
    case IntervalType.Hour: {
      return Granularity.Hour;
    }
    case IntervalType.Day: {
      return Granularity.Hour;
    }
    case IntervalType.Week: {
      return Granularity.Day;
    }
    case IntervalType.Month: {
      return Granularity.Day;
    }
    case IntervalType.IsoYear: {
      return Granularity.Week;
    }
    default: {
      throw new RangeError("No Interval set");
    }
  }
}

/**
 * Returns the Granularity for the corresponding interval type
 *
 * @param intervalType used interval type
 * @returns the granularity
 */
export function getSummarizedGranularityFromInterval(intervalType: IntervalType): Granularity {
  switch (intervalType) {
    case IntervalType.Hour: {
      return Granularity.Hour;
    }
    case IntervalType.Day: {
      return Granularity.Day;
    }
    case IntervalType.Week: {
      return Granularity.Week;
    }
    case IntervalType.Month: {
      return Granularity.Month;
    }
    case IntervalType.IsoYear: {
      return Granularity.IsoYear;
    }
    default: {
      throw new RangeError("No Interval set");
    }
  }
}

/**
 * returns calendar week from a date
 *
 * @param date date to get the week from
 * @returns calendar week as a number
 */
export function getWeekFromDate(date: Date): number {
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86_400_000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

/**
 * returns the smallest date from an array of dates
 * @param dates the array of dates
 * @returns the smallest date
 */
export function getMinDate(dates: Date[]): Date {
  return new Date(Math.min(...dates.map((val) => val.valueOf())));
}

/**
 * returns the biggest date from an array of dates
 * @param dates the array of dates
 * @returns the biggest date
 */
export function getMaxDate(dates: Date[]): Date {
  return new Date(Math.max(...dates.map((val) => val.valueOf())));
}

/**
 * Formats a date to the desired Endpoint format
 *
 * @param date date to format
 * @returns the formatted date as string
 */
export function formatToApiFormat(date: Dayjs | null): string | null {
  return date ? date.format("YYYY-MM-DDTHH:mm") : null;
}

export function timestampToPrintableFormat(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
}

const adjustMeasureTime = (value: MeasureItemDto): MeasureItemDto => {
  if (!value.measureTime) return value;

  const date = new Date(value.measureTime);
  return {
    ...value,
    measureTime: date,
  };
};

export const adjustCurrentValues = (item: MeasuresItemizedCompactItemDto): MeasuresItemizedCompactItemDto => {
  const adjustedValues = item.currentValues?.map((value) => adjustMeasureTime(value)) ?? [];
  const adjustedPastValues = item.pastValues?.map((value) => adjustMeasureTime(value)) ?? [];
  return {
    ...item,
    currentValues: adjustedValues,
    pastValues: adjustedPastValues,
  };
};

/**
 * returns the interval which should be fetched based on the selected Tab
 * @param selectedTab the selected Tab
 * @returns the interval type to fetch
 */
export function getIntervalFromSelectedTab(selectedTab: IntervalTab): IntervalType {
  switch (selectedTab) {
    case IntervalTab.Day: {
      return IntervalType.Day;
    }
    case IntervalTab.Week: {
      return IntervalType.Week;
    }
    case IntervalTab.Month: {
      return IntervalType.Month;
    }
    case IntervalTab.Year: {
      return IntervalType.IsoYear;
    }
    default: {
      throw new Error("No valid tab selected");
    }
  }
}

export function getDateRangeText(measuresData: MeasuresSummarizedDto, selectedTab: IntervalTab): string {
  if (!measuresData) return "";
  if (selectedTab === IntervalTab.Day) {
    return `${dayjs(measuresData.currentDateRange.end).format("MMM D, YYYY")} vs ${dayjs(measuresData.pastDateRange.end).format(
      "MMM D, YYYY"
    )}`;
  }
  return `${dayjs(measuresData.currentDateRange.start).format("MMM D")} - ${dayjs(measuresData.currentDateRange.end).format(
    "MMM D, YYYY"
  )} vs ${dayjs(measuresData.pastDateRange.start).format("MMM D")} - ${dayjs(measuresData.pastDateRange.end).format("MMM D, YYYY")}`;
}

export const getDateTime = (
  measuresData: MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | null | undefined,
  intervalTab: IntervalTab,
  today: Dayjs,
  t: TFunction<"translation", undefined>
): string => {
  if (!measuresData) return "";

  switch (intervalTab) {
    case IntervalTab.Day: {
      return `${t(dayjs(today).format("MMM"))}, ${dayjs(today).format("D YYYY")}`;
    }
    case IntervalTab.Week:
    case IntervalTab.Month:
    case IntervalTab.Year: {
      return `${t(dayjs(measuresData.currentDateRange.start).format("MMM"))}, ${dayjs(measuresData.currentDateRange.start).format(
        "D YYYY"
      )} - ${t(dayjs(measuresData.currentDateRange.end).add(-1, "day").format("MMM"))}, ${dayjs(measuresData.currentDateRange.end)
        .add(-1, "day")
        .format("D YYYY")}`;
    }
    default: {
      return "";
    }
  }
};

export const getMeasuresData = (
  selectedTab: IntervalTab,
  measuresDataToday: MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | undefined,
  measuresDataWeek: MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | undefined,
  measuresDataMonth: MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | undefined,
  measuresDataYear: MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | undefined
): MeasuresItemizedCompactDto | MeasuresSummarizedDto | MeasuresItemizedDto | null | undefined => {
  switch (selectedTab) {
    case IntervalTab.Day: {
      return measuresDataToday;
    }
    case IntervalTab.Week: {
      return measuresDataWeek;
    }
    case IntervalTab.Month: {
      return measuresDataMonth;
    }
    case IntervalTab.Year: {
      return measuresDataYear;
    }
    default: {
      return null;
    }
  }
};

export function formatDateStringToDayjsDate(dateString: string): Dayjs {
  return dayjs(dateString);
}

export function formatDate(date: Dayjs): string {
  return date.format("MMM, D YYYY");
}

export function formatLocalDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatLocalTimeRange(startTS: string, endTS: string, expectedLabel = "expected"): string {
  const { start, end } = formatLocalTimeRangeParts(startTS, endTS, expectedLabel);
  return `${start} ${end}`;
}

export function formatLocalTimeRangeParts(startTS: string, endTS: string, expectedLabel = "expected"): { start: string; end: string } {
  const startDate = new Date(startTS);
  const endDate = new Date(endTS);

  const end =
    startDate.toDateString() === endDate.toDateString()
      ? endDate.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
      : formatLocalDateTime(endTS);

  return { start: `${formatLocalDateTime(startTS)} –`, end: `${end} (${expectedLabel})` };
}
